import { useAtomValue, atom } from 'jotai';
import { updateQaAutoTranslateConfig } from 'services/api/admin';
import { toUpperCase } from 'utils/toLowerCase';
import { useCurrentConversationIdValue } from '../conversation';
import { ChatPreference, UserDB } from 'lib/db';
import { useLiveQuery } from 'dexie-react-hooks';
import { useCallback, useMemo } from 'react';
import { useRegionLanguageMap } from '../system/useRegionLanguage';
import { useAtomCallback } from 'jotai/utils';
import { langConfigs } from 'components/Langs/WorldLangSelect';

export enum TipsStatus {
  Show,
  Dismissed,
}

const autoTranslateConfigState = atom(false);

export const useAutoTranslateConfig = () => useAtomValue(autoTranslateConfigState);

export const useSetAutoTranslateConfig = () => {
  return useAtomCallback(
    useCallback((_, set, value: boolean, updateService = true) => {
      set(autoTranslateConfigState, value);

      return updateService ? updateQaAutoTranslateConfig(value).catch(() => {}) : Promise.resolve();
    }, []),
  );
};

const buyerLocaleAliasMap: Record<string, string> = {
  zh_CN: 'zh-CN',
  zh_TW: 'zh-TW',
  cn: 'zh-CN',
  en_US: 'en-US',
  en: 'en-US',
};

/**
 * 归一化买家语言（如 zh_CN → zh-CN），不在支持的语言列表中时返回 undefined
 */
export const normalizeBuyerLocale = (locale?: string): General.Language | undefined => {
  const normalized = (locale && buyerLocaleAliasMap[locale]) || locale;
  return normalized && langConfigs.some((item) => item[2] === normalized)
    ? (normalized as General.Language)
    : undefined;
};

export const useCurrentChatBuyerLang = (
  region: General.CaselessRegion,
  platform?: General.PlatformUpperCaseTunnel,
  /** 买家资料中的原始语言值（如 FACEBOOK 的 locale、JOLECT 的 language），由调用方显式传入 */
  buyerLocale?: string,
) => {
  const preference = useCurrentChatPreferenceValue();
  const regionLanguageMap = useRegionLanguageMap();

  return useMemo(() => {
    if (preference?.buyerLang) {
      return preference.buyerLang;
    }
    const locale = normalizeBuyerLocale(buyerLocale);
    if (platform === 'FACEBOOK') {
      return locale || regionLanguageMap.FACEBOOK || regionLanguageMap.GB;
    }
    if (platform === 'JOLECT' && locale) {
      return locale;
    }
    return regionLanguageMap[toUpperCase(region)] || regionLanguageMap.GB;
  }, [preference?.buyerLang, region, regionLanguageMap, platform, buyerLocale]);
};

export const useCurrentChatGuaranteedAssign = () => {
  const preference = useCurrentChatPreferenceValue();

  return useMemo(
    () => preference?.grantedAssignTips ?? TipsStatus.Show,
    [preference?.grantedAssignTips],
  );
};

export const useSetCurrentChatPreference = () => {
  const conversationId = useCurrentConversationIdValue();
  return (value: Omit<ChatPreference, 'conversationId'>) => {
    if (conversationId) {
      UserDB.instance?.chatPreference.put(
        {
          ...value,
          conversationId,
        },
        conversationId,
      );
    }
  };
};

export const useCurrentChatPreferenceValue = () => {
  const conversationId = useCurrentConversationIdValue();

  return useLiveQuery(() => {
    if (conversationId) {
      return UserDB.instance?.chatPreference
        .where('conversationId')
        ?.equals(conversationId)
        .first();
    }
  }, [conversationId]);
};
